import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import PuzzleClaimPanel from '../../components/PuzzleClaimPanel';
import PuzzleUploadModal from '../../components/PuzzleUploadModal';
import ProxyTargetPicker from '../../components/ProxyTargetPicker';
import TabBar from '../../components/TabBar';
import { ArrowLeft, Puzzle, PackageCheck, UploadCloud } from 'lucide-react';

// 后台 · 证券代传 / 证券代提（管理员 + 代理商）
//   代提 = 代对方领取数据包；代传 = 代对方上传三张图并识别（记录、单数都算在对方名下）
//   规则与排五代传/代提一致：管理员可不选目标（=本人）；代理商必须选择自己名下的成员或外部接单人；
//   系统按【被代传用户】当前领取的包来匹配号码，所以对方必须已经领了包（没领的话先“代提”帮他领）。
export default function PuzzleProxyPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mode = params.get('mode') === 'claim' ? 'claim' : 'upload';
  // 代提、代传各自记各自的对象，互不影响：常见情况是先给 A 代提领包，再给 B 代传图片（B 不一定是 A）。
  const [claimTarget, setClaimTarget] = useState(null);    // { id, name, ... }
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const setMode = (m) => setParams({ mode: m }, { replace: true });

  const tab = (key, label, Icon) => (
    <button key={key} onClick={() => setMode(key)} style={{
      flex: 1, height: 38, borderRadius: 10, border: 'none', cursor: 'pointer',
      background: mode === key ? 'var(--c-surface)' : 'transparent',
      color: mode === key ? 'var(--c-primary)' : 'var(--c-text-secondary)',
      fontSize: 14, fontWeight: mode === key ? 700 : 500, boxShadow: mode === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    }}><Icon size={15} /> {label}</button>
  );

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate('/admin')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}><Puzzle size={16} /> {mode === 'claim' ? '证券代提' : '证券代传'}</span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 6, padding: 4, background: 'var(--c-bg)', borderRadius: 12, marginBottom: 12 }}>
          {tab('claim', '代提（领包）', PackageCheck)}
          {tab('upload', '代传（上传）', UploadCloud)}
        </div>

        {/* 选好对象后自动折叠成一行，点“更换”再展开。代提、代传各选各的（key 不同 → 切换时各自保留自己的选择） */}
        {mode === 'claim'
          ? <ProxyTargetPicker key="claim" value={claimTarget} onChange={setClaimTarget} />
          : <ProxyTargetPicker key="upload" value={uploadTarget} onChange={setUploadTarget} />}

        {mode === 'claim' ? (
          !claimTarget ? (
            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--c-text-tertiary)', padding: 20 }}>先在上方选择代为领取的对象</p>
          ) : (
            /* 代提：只显示领包面板 */
            <PuzzleClaimPanel key={`${claimTarget.id}-${refreshKey}`} targetUser={claimTarget} allowFinish onChanged={() => setRefreshKey(k => k + 1)} />
          )
        ) : !uploadTarget ? (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--c-text-tertiary)', padding: 20 }}>先在上方选择代为上传的对象</p>
        ) : (
          /* 代传：只显示上传入口 */
          <>
            <button className="btn btn-primary" onClick={() => setUploadOpen(true)} style={{ height: 48, fontSize: 15 }}>
              <UploadCloud size={17} /> 为 {uploadTarget.name} 代传三张图片
            </button>
            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', textAlign: 'center', marginTop: 8, lineHeight: 1.6 }}>
              按【{uploadTarget.name}】当前领取的包匹配号码；对方没领包请先切到“代提”领取。代传时字段可直接修改，不做号码校验，记录算在对方名下。
            </div>
          </>
        )}
      </div>

      {uploadOpen && uploadTarget && (
        <PuzzleUploadModal
          targetUser={uploadTarget}
          onClose={() => { setUploadOpen(false); setRefreshKey(k => k + 1); }}
          onChanged={() => setRefreshKey(k => k + 1)}
        />
      )}

      <TabBar mode="admin" />
    </div>
  );
}
